import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useAuth } from './AuthProvider';
import { AppShell } from './AppShell';
import { SignInPage } from '../features/auth/SignInPage';
const DashboardPage = lazy(() =>
  import('../features/dashboard/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  })),
);
const AgendaPage = lazy(() =>
  import('../features/agenda/AgendaPage').then((module) => ({ default: module.AgendaPage })),
);
const PatientsPage = lazy(() =>
  import('../features/patients/PatientsPage').then((module) => ({ default: module.PatientsPage })),
);
const PatientFormPage = lazy(() =>
  import('../features/patients/PatientFormPage').then((module) => ({
    default: module.PatientFormPage,
  })),
);
const PatientDetailPage = lazy(() =>
  import('../features/patients/PatientDetailPage').then((module) => ({
    default: module.PatientDetailPage,
  })),
);
const FinancePage = lazy(() =>
  import('../features/finance/FinancePage').then((module) => ({ default: module.FinancePage })),
);
const TasksPage = lazy(() =>
  import('../features/tasks/TasksPage').then((module) => ({ default: module.TasksPage })),
);
const ChatPage = lazy(() =>
  import('../features/chat/ChatPage').then((module) => ({ default: module.ChatPage })),
);
const NotificationsPage = lazy(() =>
  import('../features/notifications/NotificationsPage').then((module) => ({
    default: module.NotificationsPage,
  })),
);
const SettingsPage = lazy(() =>
  import('../features/settings/SettingsPage').then((module) => ({ default: module.SettingsPage })),
);

class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // Error details are intentionally not rendered or logged with sensitive context.
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          p: 4,
          textAlign: 'center',
        }}
      >
        <Box sx={{ maxWidth: 460 }}>
          <AlertTriangle size={38} aria-hidden="true" />
          <Typography variant="h1" sx={{ mt: 4 }}>
            Algo não saiu como esperado
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            Seus dados não foram alterados. Recarregue a página para tentar novamente.
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 5 }}>
            Recarregar página
          </Button>
        </Box>
      </Box>
    );
  }
}

function ProtectedRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <Box
        sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}
        role="status"
        aria-label="Carregando sessão"
      >
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (!user) return <Navigate to="/entrar" state={{ from: location.pathname }} replace />;
  return <AppShell />;
}

export function App() {
  return (
    <AppErrorBoundary>
      <Suspense
        fallback={
          <Box
            sx={{ minHeight: 280, display: 'grid', placeItems: 'center' }}
            role="status"
            aria-label="Carregando página"
          >
            <CircularProgress size={30} />
          </Box>
        }
      >
        <Routes>
          <Route path="/entrar" element={<SignInPage />} />
          <Route element={<ProtectedRoutes />}>
            <Route index element={<DashboardPage />} />
            <Route path="agenda" element={<AgendaPage />} />
            <Route path="pacientes" element={<PatientsPage />} />
            <Route path="pacientes/novo" element={<PatientFormPage />} />
            <Route path="pacientes/:patientId" element={<PatientDetailPage />} />
            <Route path="pacientes/:patientId/editar" element={<PatientFormPage />} />
            <Route path="financeiro" element={<FinancePage />} />
            <Route path="tarefas" element={<TasksPage />} />
            <Route path="conversas" element={<ChatPage />} />
            <Route path="notificacoes" element={<NotificationsPage />} />
            <Route path="configuracoes" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppErrorBoundary>
  );
}
